// Warp Speed WebGL2 transition effect
// Extracted from standalone Warp Speed project into a controllable module

const VERT = `#version 300 es
in vec2 aP;
out vec2 vUv;
void main(){
  vUv=aP*0.5+0.5;
  gl_Position=vec4(aP,0,1);
}`

const MAIN_FRAG = `#version 300 es
precision highp float;
uniform float uTime;
uniform vec2 uRes;
uniform vec2 uMouse;
uniform float uSpeed;
uniform float uEdge;
out vec4 O;

#define PI 3.14159265
#define TAU 6.28318530
#define S smoothstep

float h11(float n){return fract(sin(n)*43758.5453);}
float h12(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
vec2 h22(vec2 p){
  p=vec2(dot(p,vec2(127.1,311.7)),dot(p,vec2(269.5,183.3)));
  return fract(sin(p)*43758.5453);
}

float noise2(vec2 p){
  vec2 i=floor(p),f=fract(p);
  f=f*f*(3.0-2.0*f);
  return mix(mix(h12(i),h12(i+vec2(1,0)),f.x),
             mix(h12(i+vec2(0,1)),h12(i+vec2(1,1)),f.x),f.y);
}

float noise3(vec3 p){
  vec3 i=floor(p),f=fract(p);
  f=f*f*(3.0-2.0*f);
  float n=i.x+i.y*157.0+113.0*i.z;
  return mix(
    mix(mix(h11(n),h11(n+1.0),f.x),mix(h11(n+157.0),h11(n+158.0),f.x),f.y),
    mix(mix(h11(n+113.0),h11(n+114.0),f.x),mix(h11(n+270.0),h11(n+271.0),f.x),f.y),
    f.z);
}

float fbm2(vec2 p){
  float f=0.0,w=0.5;
  for(int i=0;i<4;i++){f+=w*noise2(p);p*=2.1;w*=0.5;}
  return f;
}
float fbm3(vec3 p){
  float f=0.0,w=0.5;
  for(int i=0;i<4;i++){f+=w*noise3(p);p*=2.1;w*=0.5;}
  return f;
}

float voronoi(vec2 p,out vec2 mr){
  vec2 n=floor(p),f=fract(p);
  float md=8.0;
  for(int j=-1;j<=1;j++)
  for(int i=-1;i<=1;i++){
    vec2 g=vec2(float(i),float(j));
    vec2 o=h22(n+g);
    vec2 r=g+o-f;
    float d=dot(r,r);
    if(d<md){md=d;mr=r;}
  }
  return md;
}

vec3 pal(float t,float off){
  vec3 a=vec3(0.5),b=vec3(0.5),c=vec3(1.0),d=vec3(0.0+off,0.33+off,0.67+off);
  return a+b*cos(TAU*(c*t+d));
}

void main(){
  vec2 uv=(gl_FragCoord.xy-0.5*uRes)/min(uRes.x,uRes.y);
  uv+=uMouse*0.06;
  float t=uTime,spd=uSpeed;
  float r=length(uv),a=atan(uv.y,uv.x);
  float edge=uEdge;

  float sVortex  =S(0.0, 0.15,spd);
  float sStreaks  =S(0.04,0.30,spd);
  float sSparks   =S(0.10,0.40,spd);
  float sTendrils =S(0.15,0.50,spd);
  float sBgNeb    =S(0.20,0.55,spd);
  float sNebCloud =S(0.28,0.62,spd);
  float sBio      =S(0.38,0.72,spd);
  float sCrystal  =S(0.48,0.80,spd);
  float sRift     =S(0.58,0.92,spd);

  vec3 col=vec3(0.0);

  {
    float n=fbm3(vec3(uv*2.0,t*0.15))*fbm3(vec3(uv*2.0+5.0,t*0.12+10.0));
    col+=n*0.25*mix(vec3(0.08,0.03,0.18),vec3(0.03,0.08,0.25),fbm2(uv*0.5+t*0.05))*sBgNeb;
  }

  for(float layer=0.0;layer<3.0;layer++){
    float ls=(2.0+layer*0.7)*sStreaks;
    float ns=70.0+layer*35.0;
    float as=TAU/ns;
    float sid=floor((a+PI)/as);
    float sc=sid*as-PI;
    float rn=h11(sid*17.0+layer*31.0);
    float rn2=h11(sid*43.0+layer*67.0);
    float rn3=h11(sid*89.0+layer*11.0);
    float da=abs(a-sc);da=min(da,as-da);
    float w=0.0008+0.004*rn*rn;
    float streak=exp(-da*da/(2.0*w*w));
    float rs=ls*(0.3+rn2*0.7);
    float rf=1.5+rn*4.0;
    float rad=fract(r*rf-t*rs);
    float len=0.05+rn2*0.4;
    streak*=S(0.0,0.02,rad)*S(len,len*0.3,rad);
    streak*=S(0.02,0.2,r)*(0.3+r*1.4);
    vec3 sc2=pal(rn*2.0+t*0.08,layer*0.15);
    sc2=mix(sc2,vec3(1.0),rn3*0.3);
    col+=streak*sc2*0.45*sStreaks/(1.0+layer*0.4);
  }

  for(float i=0.0;i<5.0;i++){
    float ba=i*TAU/5.0+t*0.2*(1.0+0.3*sin(i));
    float na=0.4+0.3*sin(t*0.3+i*2.0);
    float ta=ba+na*noise2(vec2(r*3.0-t*1.5,i*10.0));
    float da=a-ta;da=mod(da+PI,TAU)-PI;
    float w=0.015+0.025*sin(r*10.0-t*3.0+i*1.5);
    w*=S(0.0,0.15,r)*S(1.0,0.35,r);
    float g=exp(-da*da/(2.0*w*w))*sTendrils;
    g*=0.3+0.7*pow(0.5+0.5*sin(r*15.0-t*4.0*sTendrils+i*PI/3.5),2.0);
    col+=g*pal(i/5.0+t*0.04,0.25)*0.5*(1.0+edge*1.5);
  }

  {
    vec2 wp=vec2(a*4.0/TAU,r*5.0-t*2.5*sCrystal);
    wp+=0.3*vec2(noise3(vec3(wp*2.0,t*0.3)),noise3(vec3(wp*2.0+50.0,t*0.3)));
    vec2 mr;float md=voronoi(wp*3.0,mr);
    float edge2=1.0-S(0.0,0.07,sqrt(md));
    float vis=noise3(vec3(wp*0.5,t*0.4));
    edge2*=S(0.35,0.65,vis)*S(0.05,0.2,r)*S(1.0,0.45,r)*sCrystal;
    col+=edge2*pal(mr.x*0.5+mr.y*0.5+t*0.05,0.6)*0.7*(1.0+uEdge*2.5);
  }

  {
    vec2 rp=uv*3.0;
    rp+=vec2(noise3(vec3(uv*2.0,t*0.5)),noise3(vec3(uv*2.0+100.0,t*0.5)))*0.5;
    vec2 mr1,mr2;
    float v1=voronoi(rp+vec2(t*0.3,0.0),mr1);
    float v2=voronoi(rp*1.5+vec2(0.0,t*0.4),mr2);
    float rift=exp(-v1*40.0)*exp(-v2*40.0)*15.0;
    rift*=pow(0.5+0.5*sin(t*0.8),4.0-edge*2.0)*sRift;
    float ka=atan(mr1.y,mr1.x)+t*2.0;
    vec3 rc=pal(ka/TAU,0.0)+pal(ka/TAU+0.33,0.5);
    col+=rift*min(rc,3.0)*0.35*(1.0+edge*3.0);
  }

  for(float i=0.0;i<3.0;i++){
    float ot=t*(0.5+i*0.2);
    float oa=h11(i*7.0+floor(ot*0.5))*TAU;
    float orr=fract(ot*0.3+i*0.25)*1.2;
    vec2 op=vec2(cos(oa),sin(oa))*orr;
    float d=length(uv-op);
    float n=noise2(vec2(atan(uv.y-op.y,uv.x-op.x)*3.0/TAU,t*2.0+i*5.0));
    d+=n*0.03;
    float org=exp(-d*d*80.0)*2.0+exp(-d*15.0)*0.1;
    float life=sin(fract(ot*0.3+i*0.25)*PI);
    org*=life*life*sBio;
    col+=org*pow(pal(i/3.0+t*0.1,0.8),vec3(0.7))*0.5*(1.0+edge*2.0);
  }

  for(float layer=0.0;layer<2.0;layer++){
    vec2 sp=vec2(a*(6.0+layer*3.0)/TAU,r*(4.0+layer*2.0)-t*(4.0+layer*1.5)*sSparks);
    vec2 ci=floor(sp);
    vec2 cf=fract(sp)-0.5;
    vec2 rn=h22(ci+layer*100.0);
    if(rn.x>0.65){
      float d=length(cf-(rn-0.5)*0.4);
      float spark=exp(-d*d*500.0);
      spark*=0.5+0.5*sin(t*15.0*rn.y+rn.x*TAU);
      vec3 sc=mix(pal(rn.y*4.0,layer*0.5)*3.0,vec3(1.0,0.9,0.7),0.3);
      col+=spark*sc*0.4*sSparks;
    }
  }

  {
    float vr=r*5.0;
    float vx=exp(-vr*vr)*3.0;
    float sw=sin(a*5.0-t*6.0+r*20.0)*0.5+0.5;
    vx*=(0.2+0.8*sw)*sVortex*(0.7+0.3*sin(t*2.0));
    col+=vx*mix(vec3(0.4,0.6,1.0),vec3(1.0,0.4,0.8),sin(t*0.7)*0.5+0.5);
  }

  {
    float n1=fbm3(vec3(uv*3.0+vec2(t*0.3,0),t*0.2));
    float n2=fbm3(vec3(uv*5.0-vec2(0,t*0.4),t*0.15+10.0));
    float neb=n1*n2*2.0*S(0.0,0.3,r)*S(1.2,0.4,r)*sNebCloud;
    col+=neb*mix(pal(n1+t*0.03,0.0),pal(n2+t*0.05,0.4),n1)*0.3;
  }

  col*=1.0-0.45*r*r;
  O=vec4(max(col,0.0),1.0);
}`

const BLOOM_FRAG = `#version 300 es
precision highp float;
uniform sampler2D uTex;
in vec2 vUv;
out vec4 O;
void main(){
  vec3 c=texture(uTex,vUv).rgb;
  float br=dot(c,vec3(0.2126,0.7152,0.0722));
  O=vec4(c*smoothstep(0.7,2.0,br),1.0);
}`

const BLUR_FRAG = `#version 300 es
precision highp float;
uniform sampler2D uTex;
uniform vec2 uDir;
uniform float uScale;
in vec2 vUv;
out vec4 O;
void main(){
  vec2 ts=1.0/vec2(textureSize(uTex,0));
  float w[5]=float[5](0.227027,0.1945946,0.1216216,0.054054,0.016216);
  vec3 r=texture(uTex,vUv).rgb*w[0];
  for(int i=1;i<5;i++){
    vec2 off=uDir*ts*float(i)*uScale;
    r+=texture(uTex,vUv+off).rgb*w[i];
    r+=texture(uTex,vUv-off).rgb*w[i];
  }
  O=vec4(r,1.0);
}`

const COMP_FRAG = `#version 300 es
precision highp float;
uniform sampler2D uScene;
uniform sampler2D uBloom;
uniform vec2 uRes;
uniform float uTime;
uniform float uSpeed;
uniform float uEdge;
in vec2 vUv;
out vec4 O;

float h12(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}

void main(){
  vec2 dir=vUv-0.5;
  float dist=length(dir);
  float ab=dist*dist*0.015*(1.0+uSpeed*2.0)*(1.0+uEdge*5.0);
  float barrel=1.0+uEdge*0.08*uSpeed;
  vec2 distUv=0.5+(vUv-0.5)*barrel;

  vec3 sc;
  sc.r=texture(uScene,0.5+(distUv-0.5)*(1.0+ab)).r;
  sc.g=texture(uScene,distUv).g;
  sc.b=texture(uScene,0.5+(distUv-0.5)*(1.0-ab)).b;

  vec3 bl=texture(uBloom,vUv).rgb;
  vec3 c=sc+bl*0.8;

  vec3 mb=vec3(0.0);
  for(float i=0.0;i<6.0;i++){
    float s=1.0+i*0.006*uSpeed;
    mb+=texture(uScene,(vUv-0.5)*s+0.5).rgb;
  }
  c=mix(c,mb/6.0,0.15*uSpeed);

  c=(c*(2.51*c+0.03))/(c*(2.43*c+0.59)+0.14);
  c+=(h12(vUv*uRes+fract(uTime)*100.0)*0.025-0.0125)*uSpeed;
  c*=1.0-0.35*dist*dist;
  c=pow(clamp(c,0.0,1.0),vec3(1.0/2.2));
  O=vec4(c,1.0);
}`

// ═══════════════════════════════════════════════════════════════
// WARP TRANSITION CLASS
// ═══════════════════════════════════════════════════════════════

export class WarpTransition {
  constructor(canvas) {
    this.canvas = canvas
    this.gl = canvas.getContext('webgl2', { antialias: false, alpha: false })
    if (!this.gl) throw new Error('WebGL2 required for warp effect')

    this.speed = 0
    this.targetSpeed = 0
    this.running = false
    this._resolveDisengage = null

    const gl = this.gl
    gl.getExtension('EXT_color_buffer_float')
    this.useFloat = !!gl.getExtension('EXT_color_buffer_float')
    this.DPR = Math.min(devicePixelRatio, 2)

    this._initShaders()
    this._initQuad()
    this._resize()
    window.addEventListener('resize', () => this._resize())
  }

  _compileShader(type, src) {
    const gl = this.gl
    const s = gl.createShader(type)
    gl.shaderSource(s, src)
    gl.compileShader(s)
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      console.error(gl.getShaderInfoLog(s))
      return null
    }
    return s
  }

  _createProgram(vs, fs) {
    const gl = this.gl
    const p = gl.createProgram()
    gl.attachShader(p, this._compileShader(gl.VERTEX_SHADER, vs))
    gl.attachShader(p, this._compileShader(gl.FRAGMENT_SHADER, fs))
    gl.bindAttribLocation(p, 0, 'aP')
    gl.linkProgram(p)
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      console.error(gl.getProgramInfoLog(p))
      return null
    }
    return p
  }

  _getUniforms(prog, names) {
    const gl = this.gl
    const u = {}
    for (const n of names) u[n] = gl.getUniformLocation(prog, n)
    return u
  }

  _createFBO(w, h) {
    const gl = this.gl
    const tex = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_2D, tex)
    if (this.useFloat) {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16F, w, h, 0, gl.RGBA, gl.HALF_FLOAT, null)
    } else {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null)
    }
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    const fb = gl.createFramebuffer()
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb)
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0)
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    return { fb, tex, w, h }
  }

  _destroyFBO(fbo) {
    if (!fbo) return
    this.gl.deleteTexture(fbo.tex)
    this.gl.deleteFramebuffer(fbo.fb)
  }

  _initShaders() {
    this.pMain = this._createProgram(VERT, MAIN_FRAG)
    this.pBloom = this._createProgram(VERT, BLOOM_FRAG)
    this.pBlur = this._createProgram(VERT, BLUR_FRAG)
    this.pComp = this._createProgram(VERT, COMP_FRAG)

    this.uMain = this._getUniforms(this.pMain, ['uTime', 'uRes', 'uMouse', 'uSpeed', 'uEdge'])
    this.uBloom = this._getUniforms(this.pBloom, ['uTex'])
    this.uBlur = this._getUniforms(this.pBlur, ['uTex', 'uDir', 'uScale'])
    this.uComp = this._getUniforms(this.pComp, ['uScene', 'uBloom', 'uRes', 'uTime', 'uSpeed', 'uEdge'])
  }

  _initQuad() {
    const gl = this.gl
    this.quadVAO = gl.createVertexArray()
    gl.bindVertexArray(this.quadVAO)
    const buf = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, buf)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW)
    gl.enableVertexAttribArray(0)
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)
  }

  _drawQuad() {
    this.gl.bindVertexArray(this.quadVAO)
    this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4)
  }

  _resize() {
    this._destroyFBO(this.fboScene)
    this._destroyFBO(this.fboBlurA)
    this._destroyFBO(this.fboBlurB)
    this.canvas.width = Math.floor(innerWidth * this.DPR)
    this.canvas.height = Math.floor(innerHeight * this.DPR)
    const w = this.canvas.width, h = this.canvas.height
    const bw = Math.floor(w / 2), bh = Math.floor(h / 2)
    this.fboScene = this._createFBO(w, h)
    this.fboBlurA = this._createFBO(bw, bh)
    this.fboBlurB = this._createFBO(bw, bh)
  }

  // ─── Public API ───

  engage() {
    this.targetSpeed = 1.0
    this.canvas.classList.add('active')

    if (!this.running) {
      this.running = true
      this._renderLoop(performance.now())
    }

    return new Promise(resolve => {
      const check = () => {
        if (this.speed > 0.85) resolve()
        else requestAnimationFrame(check)
      }
      check()
    })
  }

  disengage() {
    this.targetSpeed = 0

    return new Promise(resolve => {
      this._resolveDisengage = resolve
    })
  }

  hold() {
    this.targetSpeed = 1.0
    this.canvas.classList.add('active')

    if (!this.running) {
      this.running = true
      this._renderLoop(performance.now())
    }
  }

  release() {
    return this.disengage()
  }

  _renderLoop(now) {
    if (!this.running) return
    requestAnimationFrame((t) => this._renderLoop(t))

    const t = now * 0.001
    const gl = this.gl

    // Faster easing for transitions (0.035 vs original 0.012)
    this.speed += (this.targetSpeed - this.speed) * 0.035

    if (this.speed < 0.003 && this.targetSpeed === 0) {
      this.speed = 0
      this.running = false
      this.canvas.classList.remove('active')
      if (this._resolveDisengage) {
        this._resolveDisengage()
        this._resolveDisengage = null
      }
      return
    }

    const edge = 0.3
    const W = this.canvas.width, H = this.canvas.height

    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fboScene.fb)
    gl.viewport(0, 0, W, H)
    gl.useProgram(this.pMain)
    gl.uniform1f(this.uMain.uTime, t)
    gl.uniform2f(this.uMain.uRes, W, H)
    gl.uniform2f(this.uMain.uMouse, 0, 0)
    gl.uniform1f(this.uMain.uSpeed, this.speed)
    gl.uniform1f(this.uMain.uEdge, edge)
    this._drawQuad()

    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fboBlurA.fb)
    gl.viewport(0, 0, this.fboBlurA.w, this.fboBlurA.h)
    gl.useProgram(this.pBloom)
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, this.fboScene.tex)
    gl.uniform1i(this.uBloom.uTex, 0)
    this._drawQuad()

    gl.useProgram(this.pBlur)
    for (let iter = 0; iter < 2; iter++) {
      const sc = 1.5 + iter * 2.0

      gl.bindFramebuffer(gl.FRAMEBUFFER, this.fboBlurB.fb)
      gl.viewport(0, 0, this.fboBlurB.w, this.fboBlurB.h)
      gl.activeTexture(gl.TEXTURE0)
      gl.bindTexture(gl.TEXTURE_2D, this.fboBlurA.tex)
      gl.uniform1i(this.uBlur.uTex, 0)
      gl.uniform2f(this.uBlur.uDir, 1.0, 0.0)
      gl.uniform1f(this.uBlur.uScale, sc)
      this._drawQuad()

      gl.bindFramebuffer(gl.FRAMEBUFFER, this.fboBlurA.fb)
      gl.viewport(0, 0, this.fboBlurA.w, this.fboBlurA.h)
      gl.bindTexture(gl.TEXTURE_2D, this.fboBlurB.tex)
      gl.uniform2f(this.uBlur.uDir, 0.0, 1.0)
      gl.uniform1f(this.uBlur.uScale, sc)
      this._drawQuad()
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    gl.viewport(0, 0, W, H)
    gl.useProgram(this.pComp)
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, this.fboScene.tex)
    gl.uniform1i(this.uComp.uScene, 0)
    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(gl.TEXTURE_2D, this.fboBlurA.tex)
    gl.uniform1i(this.uComp.uBloom, 1)
    gl.uniform2f(this.uComp.uRes, W, H)
    gl.uniform1f(this.uComp.uTime, t)
    gl.uniform1f(this.uComp.uSpeed, this.speed)
    gl.uniform1f(this.uComp.uEdge, edge)
    this._drawQuad()
  }
}
